import React, { useState, useRef, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import Toast from "../toast";
import Button from "../../components/inputs/button";
import { exportCohorts, importCohorts } from "../../services/cohort";
import { formatEquativPrice } from "../../utilities/equativPrice";
import xGreenIcon from "../../assets/svgs/x-green.svg";
import searchIcon from "../../assets/svgs/search.svg";
import "./index.scss";

// Server-driven bulk export/import (ASB-3164). Two distinct entrypoints, because the
// operations have different semantics:
//   * BulkExport — selection-driven, opened from the Manager's row Actions menu.
//   * BulkImport — selection-independent (keyed off the signed keys inside the file),
//     opened from a standalone toolbar button. Full-bleed review surface that previews
//     the dry-run plan as a PR-grade diff before an all-or-nothing apply (B-lite + C).

// status -> display. Sort weight orders the rail: problems first, no-ops last.
const STATUS_META = {
	error: { label: "Error", cls: "error", weight: 0 },
	conflict: { label: "Conflict", cls: "conflict", weight: 1 },
	ok: { label: "Will update", cls: "ok", weight: 2 },
	unchanged: { label: "No change", cls: "unchanged", weight: 3 },
};

const META_FIELD_LABEL = {
	name: "Name",
	description: "Description",
	status: "Status",
	ttl: "TTL (days)",
	membership_mode: "Membership",
	contextual_only: "Contextual only",
	destinations: "Destinations",
	gam_networks: "GAM networks",
	equativ_price: "Equativ Price",
	combine: "Combine groups",
};

function triggerDownload(blob, filename) {
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	a.style.visibility = "hidden";
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}

function summarize(a) {
	if (a.status === "conflict" || a.status === "error") {
		return (a.errors || []).join("; ");
	}
	if (a.status === "unchanged") return "No changes";
	const s = a.summary || {};
	const parts = [];
	if (s.edited) parts.push(`${s.edited} criteria edited`);
	if (s.added) parts.push(`${s.added} added`);
	if (s.deleted) parts.push(`${s.deleted} deleted`);
	if (s.metadata) parts.push("settings changed");
	if (s.combine) parts.push("grouping changed");
	const text = parts.join(", ") || "Will update";
	return text.charAt(0).toUpperCase() + text.slice(1);
}

function fmtVal(v) {
	if (v === null || v === undefined || v === "") return "—";
	return String(v);
}

// fmtVal is generic across every meta field; equativ_price needs its own branch because the
// backend diff carries it as a bare float (1, 2.5, 0.75) and every other money display in the
// app is two-decimal-formatted (ASB-3872 review) — without this, a price-only edit's preview
// row would read "Equativ Price  1 → 2.5" instead of "1.00 → 2.50".
function fmtMetaVal(field, v) {
	if (field === "equativ_price") {
		if (v === null || v === undefined || v === "") return "—";
		return formatEquativPrice(v);
	}
	return fmtVal(v);
}

// Turn an API/fetch error into a user-facing message. A specific server message (e.g. a 400
// validation error) is shown as-is; opaque failures — 5xx, network errors, or the browser's
// "Load failed"/"Failed to fetch" — get a generic line with support guidance, matching the
// phrasing used elsewhere in the app.
function errorMessage(err, action) {
	const generic = `Something went wrong ${action}. Please reach out to your ArcSpan representative for assistance.`;
	if (!err || err.isNetworkError || (err.status && err.status >= 500)) return generic;
	const msg = (err.data && err.data.message) || err.message;
	if (!msg || msg === "Load failed" || msg === "Failed to fetch") return generic;
	return msg;
}

// One settings change: field, before → after.
function MetaRow({ change }) {
	return (
		<div className="diff-meta-row">
			<span className="diff-meta-row__field p4">
				{META_FIELD_LABEL[change.field] || change.field}
			</span>
			<span className="diff-val diff-val--before p4">{fmtMetaVal(change.field, change.before)}</span>
			<span className="diff-arrow p4">→</span>
			<span className="diff-val diff-val--after p4">{fmtMetaVal(change.field, change.after)}</span>
		</div>
	);
}

MetaRow.propTypes = { change: PropTypes.object.isRequired };

// One rule change. edited = before/after; added = after only; deleted = before only.
function RuleRow({ rule }) {
	const op = rule.op;
	const groupLabel =
		op === "edited" && rule.group_before != null && rule.group_before !== rule.group
			? `Group ${rule.group_before} → ${rule.group}`
			: `Group ${rule.group}`;
	return (
		<div className={`diff-rule diff-rule--${op}`}>
			<div className="diff-rule__head">
				<span className={`diff-badge diff-badge--${op}`}>{op}</span>
				<span className="diff-rule__group p4">{groupLabel}</span>
			</div>
			{rule.before != null && (
				<code className="diff-line diff-line--del">- {rule.before}</code>
			)}
			{rule.after != null && (
				<code className="diff-line diff-line--add">+ {rule.after}</code>
			)}
		</div>
	);
}

RuleRow.propTypes = { rule: PropTypes.object.isRequired };

// The PR-grade diff for one audience (the right pane).
function CohortDiff({ audience }) {
	const a = audience;
	const diff = a.diff || { metadata: [], rules: [] };
	const meta = diff.metadata || [];
	const changedRules = (diff.rules || []).filter((r) => r.op !== "unchanged");
	const sm = STATUS_META[a.status] || { label: a.status, cls: a.status };

	return (
		<div className="diff-pane">
			<div className="diff-pane__header">
				<h3 className="p2 strong">{a.name || a.audience_id}</h3>
				<span className={`diff-status diff-status--${sm.cls}`}>{sm.label}</span>
				<span className="diff-id">
					<span className="diff-id__label">ID</span>
					<span className="diff-id__value">{a.audience_id}</span>
				</span>
			</div>

			{(a.status === "error" || a.status === "conflict") && (a.errors || []).length > 0 && (
				<div className="diff-banner diff-banner--error">
					<p className="p4 strong">
						{a.status === "conflict" ? "Conflict — Re-export Needed" : "Cannot Apply"}
					</p>
					<ul>
						{a.errors.map((e, i) => (
							<li
								key={i}
								className="p4"
							>
								{e}
							</li>
						))}
					</ul>
				</div>
			)}

			{a.status === "unchanged" && (
				<p className="diff-empty p3">No changes detected for this audience.</p>
			)}

			{meta.length > 0 && (
				<section className="diff-section">
					<p className="diff-section__title p4 strong">Settings</p>
					{meta.map((c, i) => (
						<MetaRow
							key={i}
							change={c}
						/>
					))}
				</section>
			)}

			{changedRules.length > 0 && (
				<section className="diff-section">
					<p className="diff-section__title p4 strong">Criteria</p>
					{changedRules.map((r, i) => (
						<RuleRow
							key={i}
							rule={r}
						/>
					))}
				</section>
			)}
		</div>
	);
}

CohortDiff.propTypes = { audience: PropTypes.object.isRequired };

// --------------------------------------------------------------------------- //
// Export — lightweight, selection-driven (Manager Actions menu).
// --------------------------------------------------------------------------- //
function BulkExport({ cohort, tagCohorts, onUpdate }) {
	const tagId = localStorage.getItem("activeSilo");
	const selectedIds = (cohort || []).map((c) => c.value);

	const [exporting, setExporting] = useState(false);
	const [error, setError] = useState(null);
	const [toast, setToast] = useState(null);

	const close = () => {
		if (tagCohorts) tagCohorts([]);
		onUpdate();
	};

	// Modal behavior (matches the Import modal): lock background scroll, close on Esc.
	useEffect(() => {
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev || "unset";
		};
	}, []);

	useEffect(() => {
		const onKey = (e) => {
			if (e.key === "Escape" && !exporting) close();
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [exporting]); // eslint-disable-line

	const handleExport = async () => {
		setError(null);
		setExporting(true);
		try {
			const { blob, filename, skipped } = await exportCohorts(tagId, selectedIds);
			triggerDownload(blob, filename);
			if (skipped.length) {
				setToast({
					status: "Info",
					title: "Some audiences skipped",
					message: `${skipped.length} audience(s) were skipped — auto-generated and modeled audiences cannot be bulk-edited.`,
				});
				return;
			}
			close();
		} catch (err) {
			setError(errorMessage(err, "exporting"));
		} finally {
			setExporting(false);
		}
	};

	if (toast) {
		return createPortal(
			<div id="bulk-export-box">
				<Toast
					status={toast.status}
					title={toast.title}
					message={toast.message}
					callback={close}
					autoHide={false}
				/>
			</div>,
			document.body
		);
	}

	return createPortal(
		<div id="bulk-export-box">
			<div className="bulk-io-overlay">
				<div className="bulk-io-surface bulk-io-surface--compact">
					<header className="bulk-io-topbar">
						<h2>Export for Bulk Edit</h2>
						<button
							type="button"
							className="bulk-io-close"
							aria-label="Close"
							onClick={close}
							disabled={exporting}
						>
							<img
								src={xGreenIcon}
								alt="Close"
							/>
						</button>
					</header>

					<div className="bulk-io-body">
						<p className="p3">
							{selectedIds.length
								? `Export ${selectedIds.length} selected audience${selectedIds.length !== 1 ? "s" : ""}`
								: "Export all editable audiences for this tag"}{" "}
							as an Excel (.xlsx) workbook. Edit it offline, then re-import it from the
							Import Edits button.
						</p>
						<p className="p4 bulk-io-note">
							Please leave the locked columns (audience_id, audience_key, criteria_key) as they
							are — they match your changes back to the right audience on import. Edit
							anything else freely.
						</p>
						{error && <p className="bulk-io-error p3">{error}</p>}
					</div>

					<footer className="bulk-io-footer bulk-io-footer--end">
						<div className="bulk-io-footer__actions">
							<button
								type="button"
								className="cancel-link"
								onClick={close}
								disabled={exporting}
							>
								Cancel
							</button>
							<Button
								text="Export"
								type="button"
								style="dark"
								disabled={exporting}
								loaded={exporting}
								onClick={handleExport}
							/>
						</div>
					</footer>
				</div>
			</div>
		</div>,
		document.body
	);
}

BulkExport.propTypes = {
	cohort: PropTypes.array,
	tagCohorts: PropTypes.func,
	onUpdate: PropTypes.func.isRequired,
};

// --------------------------------------------------------------------------- //
// Import — selection-independent, full-bleed review surface (toolbar button).
// --------------------------------------------------------------------------- //
function BulkImport({ getCohorts, onUpdate }) {
	const tagId = localStorage.getItem("activeSilo");

	const [importing, setImporting] = useState(false);
	const [applying, setApplying] = useState(false);
	const [reexporting, setReexporting] = useState(false);
	const [plan, setPlan] = useState(null);
	const [error, setError] = useState(null);
	const [toast, setToast] = useState(null);
	const [search, setSearch] = useState("");
	const [selectedId, setSelectedId] = useState(null);
	const [dragActive, setDragActive] = useState(false);
	const fileInputRef = useRef(null);
	const fileRef = useRef(null);

	const audiences = plan ? plan.audiences || [] : [];

	// Rail order: problems first, then updates, then no-ops; stable name sort within a tier.
	const sortedAudiences = useMemo(() => {
		const term = search.trim().toLowerCase();
		return [...audiences]
			.filter(
				(a) =>
					!term ||
					(a.name || "").toLowerCase().includes(term) ||
					String(a.audience_id).toLowerCase().includes(term)
			)
			.sort((a, b) => {
				const wa = (STATUS_META[a.status] || {}).weight ?? 9;
				const wb = (STATUS_META[b.status] || {}).weight ?? 9;
				if (wa !== wb) return wa - wb;
				return (a.name || "").localeCompare(b.name || "");
			});
	}, [audiences, search]);

	// When a fresh plan loads, focus the most important audience (first problem, else first update).
	useEffect(() => {
		if (!plan) {
			setSelectedId(null);
			return;
		}
		const ordered = [...audiences].sort(
			(a, b) =>
				((STATUS_META[a.status] || {}).weight ?? 9) -
				((STATUS_META[b.status] || {}).weight ?? 9)
		);
		setSelectedId(ordered.length ? ordered[0].audience_id : null);
	}, [plan]);

	// Modal behavior: lock background scroll while open, and close on Esc (never while applying).
	useEffect(() => {
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev || "unset";
		};
	}, []);

	useEffect(() => {
		const onKey = (e) => {
			if (e.key === "Escape" && !applying) onUpdate();
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [applying, onUpdate]);

	const selectedAudience = audiences.find((a) => a.audience_id === selectedId) || null;

	const ingestFile = async (file) => {
		if (!file) return;
		setError(null);
		setPlan(null);
		fileRef.current = file;
		setImporting(true);
		try {
			const result = await importCohorts(file, true);
			setPlan(result);
		} catch (err) {
			setError(errorMessage(err, "reading the file"));
			fileRef.current = null;
		}
		setImporting(false);
	};

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		e.target.value = ""; // allow re-selecting the same file
		ingestFile(file);
	};

	const handleDragOver = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (!dragActive) setDragActive(true);
	};

	const handleDragLeave = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);
	};

	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);
		const file = e.dataTransfer.files && e.dataTransfer.files[0];
		if (file) ingestFile(file);
	};

	const totals = plan ? plan.totals || {} : {};
	const rowErrors = plan ? plan.row_errors || [] : [];
	// Conflicts mean the file is stale (re-export to fix); errors mean bad edits in the file
	// (the user must fix them — re-exporting won't help). Both block apply, but they need
	// different next steps.
	const hasConflicts = audiences.some((a) => a.status === "conflict");
	const hasErrors = rowErrors.length > 0 || audiences.some((a) => a.status === "error");
	const blocked = !plan || hasConflicts || hasErrors;
	const applyCount = audiences.filter((a) => a.status === "ok").length;

	const handleApply = async () => {
		if (!fileRef.current || blocked || applyCount === 0) return;
		setApplying(true);
		setError(null);
		try {
			const result = await importCohorts(fileRef.current, false);
			const n = (result.applied || []).length;
			setToast({
				status: "Success",
				title: "Success",
				message: `${n} audience${n !== 1 ? "s" : ""} updated.`,
			});
		} catch (err) {
			setError(errorMessage(err, "applying your changes"));
		}
		setApplying(false);
	};

	// Conflicts: re-export the same audiences so the file reflects current server state, then
	// reset to the drop zone so the user can re-apply their edits and import the fresh file.
	const handleReexport = async () => {
		setReexporting(true);
		setError(null);
		try {
			const ids = audiences.map((a) => a.audience_id);
			const { blob, filename } = await exportCohorts(tagId, ids);
			triggerDownload(blob, filename);
			fileRef.current = null;
			setPlan(null);
			setSearch("");
		} catch (err) {
			setError(errorMessage(err, "re-exporting"));
		}
		setReexporting(false);
	};

	const close = () => onUpdate();

	const toastClose = () => {
		setToast(null);
		if (getCohorts) getCohorts(tagId);
		close();
	};

	if (toast) {
		return createPortal(
			<div id="bulk-import-box">
				<Toast
					status={toast.status}
					title={toast.title}
					message={toast.message}
					callback={toastClose}
					autoHide={false}
				/>
			</div>,
			document.body
		);
	}

	return createPortal(
		<div id="bulk-import-box">
			<div className="bulk-io-overlay">
				<div className="bulk-io-surface">
					<header className="bulk-io-topbar">
						<h2>Import Edits</h2>
						<button
							type="button"
							className="bulk-io-close"
							aria-label="Close"
							onClick={close}
							disabled={applying}
						>
							<img
								src={xGreenIcon}
								alt="Close"
							/>
						</button>
					</header>

					{/* Hidden input shared by the drop zone and the re-pick button. */}
					<input
						ref={fileInputRef}
						type="file"
						accept=".xlsx"
						style={{ display: "none" }}
						onChange={handleFileChange}
					/>

					{!plan && (
						<div className="bulk-io-empty">
							<div
								className={`bulk-io-dropzone ${dragActive ? "is-drag" : ""}`}
								onDragOver={handleDragOver}
								onDragLeave={handleDragLeave}
								onDrop={handleDrop}
								onClick={() => fileInputRef.current && fileInputRef.current.click()}
							>
								<svg
									className="bulk-io-dropzone__icon"
									width="40"
									height="40"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
									<polyline points="17 8 12 3 7 8" />
									<line
										x1="12"
										y1="3"
										x2="12"
										y2="15"
									/>
								</svg>
								{importing ? (
									<div className="bulk-io-dropzone__text">Checking changes…</div>
								) : (
									<>
										<div className="bulk-io-dropzone__text">
											Drag your edited file here, or click to browse
										</div>
										<div className="bulk-io-dropzone__sub p4">
											Excel (.xlsx) workbook
										</div>
									</>
								)}
							</div>
							<p className="bulk-io-hint p4">
								New here? In the Audience Manager, select one or more audiences to
								enable the <strong>Actions</strong> menu, then choose{" "}
								<strong>Export for Bulk Edit</strong> to download them — then drop the
								edited workbook here.
							</p>
							{error && <p className="bulk-io-error p3">{error}</p>}
						</div>
					)}

					{plan && error && <p className="bulk-io-error p3">{error}</p>}

					{plan && (
						<div className="bulk-io-review">
							<aside className="bulk-io-rail">
								<div className="bulk-io-railsearch">
									<img
										src={searchIcon}
										alt=""
										className="bulk-io-railsearch__icon"
									/>
									<input
										className="bulk-io-railsearch__input"
										type="text"
										placeholder="Filter audiences…"
										value={search}
										onChange={(e) => setSearch(e.target.value)}
									/>
								</div>
								<ul className="bulk-io-raillist">
									{sortedAudiences.map((a) => {
										const sm = STATUS_META[a.status] || { cls: a.status };
										return (
											<li key={a.audience_id}>
												<button
													type="button"
													className={`bulk-io-railitem ${
														a.audience_id === selectedId ? "is-selected" : ""
													}`}
													onClick={() => setSelectedId(a.audience_id)}
												>
													<span
														className={`bulk-io-dot bulk-io-dot--${sm.cls}`}
													/>
													<span className="bulk-io-railitem__name p4">
														{a.name || a.audience_id}
													</span>
													<span className="bulk-io-railitem__id">
														ID {a.audience_id}
													</span>
													<span className="bulk-io-railitem__summary p4">
														{summarize(a)}
													</span>
												</button>
											</li>
										);
									})}
									{sortedAudiences.length === 0 && (
										<li className="bulk-io-railempty p4">No matching audiences.</li>
									)}
								</ul>
							</aside>

							<section className="bulk-io-detail">
								{rowErrors.length > 0 && (
									<div className="diff-banner diff-banner--error">
										<p className="p4 strong">
											{rowErrors.length} file row error
											{rowErrors.length !== 1 ? "s" : ""} — fix and re-import:
										</p>
										<ul>
											{rowErrors.map((e, i) => (
												<li
													key={i}
													className="p4"
												>
													{e.sheet
														? `${e.sheet[0].toUpperCase()}${e.sheet.slice(1)} row ${e.row}: `
														: `Row ${e.row}: `}
													{e.error}
												</li>
											))}
										</ul>
									</div>
								)}
								{selectedAudience ? (
									<CohortDiff audience={selectedAudience} />
								) : (
									<p className="diff-empty p3">Select an audience to see its changes.</p>
								)}
							</section>
						</div>
					)}

					<footer className="bulk-io-footer">
						<div className="bulk-io-totals p4">
							{plan && (
								<>
									<span className="bulk-io-tally bulk-io-tally--ok">
										{totals.ok || 0} to update
									</span>
									<span className="bulk-io-tally">
										{totals.unchanged || 0} unchanged
									</span>
									{totals.conflict ? (
										<span className="bulk-io-tally bulk-io-tally--conflict">
											{totals.conflict} conflict{totals.conflict !== 1 ? "s" : ""}
										</span>
									) : null}
									{totals.error ? (
										<span className="bulk-io-tally bulk-io-tally--error">
											{totals.error} error{totals.error !== 1 ? "s" : ""}
										</span>
									) : null}
									{rowErrors.length ? (
										<span className="bulk-io-tally bulk-io-tally--error">
											{rowErrors.length} row error
											{rowErrors.length !== 1 ? "s" : ""}
										</span>
									) : null}
								</>
							)}
						</div>
						<div className="bulk-io-footer__actions">
							<button
								type="button"
								className="cancel-link"
								onClick={close}
								disabled={applying || reexporting}
							>
								Cancel
							</button>
							{plan && hasConflicts && (
								<Button
									text="Re-export Updated File"
									type="button"
									style="light"
									disabled={reexporting}
									loaded={reexporting}
									onClick={handleReexport}
								/>
							)}
							{plan && !hasConflicts && (
								<Button
									text={
										!blocked && applyCount
											? `Apply ${applyCount} change${applyCount !== 1 ? "s" : ""}`
											: "Apply"
									}
									type="button"
									style="dark"
									disabled={blocked || applyCount === 0 || applying}
									loaded={applying}
									onClick={handleApply}
								/>
							)}
						</div>
					</footer>
				</div>
			</div>
		</div>,
			document.body
	);
}

BulkImport.propTypes = {
	getCohorts: PropTypes.func,
	onUpdate: PropTypes.func.isRequired,
};

export { BulkExport, BulkImport };
